<script setup lang="ts">
import { Button } from '~/components/ui/button'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '~/components/ui/sheet'
import { SidebarMenuItem } from '~/components/ui/sidebar-menu'
import type { IProps } from './types'

// Mobile replacement for a sidebar menu, which drops below the content under `xl`
const props = withDefaults(defineProps<IProps>(), {
  visibleCount: 3
})

const route = useRoute()

// NuxtLinkLocale drops params on alias routes (e.g. `/credits/:type`), so the prefix is added manually
const { getPathWithLocale } = useLocalizedPath()

const visibleItems = computed(() => props.items.slice(0, props.visibleCount))

const isActive = (url: string) => route.path === getPathWithLocale(url)
</script>

<template>
  <div v-if="items.length" class="flex flex-wrap items-center gap-2 xl:hidden">
    <NuxtLink
      v-for="item in visibleItems"
      :key="item.url"
      :to="getPathWithLocale(item.url)"
      class="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-100 px-3 py-2 text-sm font-semibold transition-all hover:bg-blue-600 hover:text-white [&_svg]:shrink-0"
      :class="isActive(item.url) ? 'bg-blue-600 text-white' : 'bg-white'"
    >
      <i v-if="item.icon" class="[&>svg]:size-4"><UiIcon :name="item.icon" /></i>
      {{ item.title }}
    </NuxtLink>

    <Sheet v-if="items.length > visibleCount">
      <SheetTrigger as-child>
        <Button variant="white" size="auto" class="shrink-0 rounded-lg px-3 py-2">
          {{ $t('common.more_categories') }}
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" class="max-h-[85vh] rounded-t-2xl">
        <SheetHeader>
          <SheetTitle class="pr-10 text-left">{{ sheetTitle }}</SheetTitle>
        </SheetHeader>
        <div class="space-y-2 overflow-y-auto">
          <SheetClose v-for="item in items" :key="item.url" as-child>
            <SidebarMenuItem :url="item.url" :icon="item.icon" :is-active="isActive(item.url)">
              {{ item.title }}
            </SidebarMenuItem>
          </SheetClose>
        </div>
      </SheetContent>
    </Sheet>
  </div>
</template>
